import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MobileRoomPrizeImage } from '../room-prize-image/mobile-room-prize-image';

import { OptimizedImage } from 'shared/components/ui/optimized-image/optimized-image';
import { InfiniteMaxProgressBar } from 'shared/components/ui/progress-bar/progress-bar';
import { WinnerStatusEnum } from 'shared/constants/shared-enums';
import { useAccount, usePublicClient } from 'shared/hooks/use-web3';
import { useQuestsStore } from 'shared/store/questsStore';

import defeatIcon from 'assets/png/final/defeat.png?format=webp;avif&as=picture';
import victoryIcon from 'assets/png/final/victory.png?format=webp;avif&as=picture';

import { defeatMessages, victoryMessages } from './mobile-finish-view.constants';

const MobileFinishView = ({
  amIWinner,
  winnerStatus,
}: {
  amIWinner: boolean;
  winnerStatus: WinnerStatusEnum;
}) => {
  const { t } = useTranslation();
  const { address } = useAccount();
  const publicClient = usePublicClient();

  const { pointBalance, snapshotPoints, fetchUserBalances } = useQuestsStore();

  const [shouldShowMessage, setShouldShowMessage] = useState(true);
  const [displayedPoints, setDisplayedPoints] = useState<number>(Number(snapshotPoints));

  useEffect(() => {
    fetchUserBalances(publicClient, address);
  }, [publicClient, address]);

  useEffect(() => {
    if (pointBalance > snapshotPoints) {
      setDisplayedPoints(Number(snapshotPoints));

      const timeout = setTimeout(() => {
        setDisplayedPoints(Number(pointBalance));
      }, 2000);

      return () => clearTimeout(timeout);
    } else {
      setDisplayedPoints(Number(pointBalance));
    }
  }, [pointBalance, snapshotPoints]);

  // Memoize message selection to avoid re-calculating on every render
  const finishMessage = useMemo(() => {
    if (
      winnerStatus === WinnerStatusEnum.TechnicalWinnerA ||
      winnerStatus === WinnerStatusEnum.TechnicalWinnerB
    ) {
      return amIWinner
        ? t('gameFinishViews.opponentDidntApprove')
        : t('gameFinishViews.didntMakeMove');
    }

    const messages = amIWinner ? victoryMessages : defeatMessages;
    const randomIndex = Math.floor(Math.random() * messages.length);
    return t(messages[randomIndex]);
  }, [amIWinner, winnerStatus, t]);

  // Find height of screen and determine if message should be shown
  useEffect(() => {
    const updateScreenHeight = () => {
      const height = window.innerHeight;
      // TODO: find optimal height for all possible  finish view states
      // Don't show message if height is less than 700px (adjust as needed)
      setShouldShowMessage(height >= 700);
    };

    // Initial height calculation
    updateScreenHeight();

    // Listen for window resize
    window.addEventListener('resize', updateScreenHeight);

    return () => {
      window.removeEventListener('resize', updateScreenHeight);
    };
  }, [finishMessage]);

  return (
    <>
      <div className="mb-4 flex justify-center">
        <OptimizedImage
          imageData={amIWinner ? victoryIcon : defeatIcon}
          alt={amIWinner ? 'Victory' : 'Defeat'}
          className={`w-full max-w-[20rem]`}
          data-testid={`finish-view-${amIWinner ? 'victory' : 'defeat'}`}
        />
      </div>
      {/* Only show message if screen height is sufficient */}
      {shouldShowMessage && (
        <p className="relative mx-auto max-w-[80%] self-stretch text-center text-md font-normal text-light-grape sm:text-base">
          {finishMessage}
        </p>
      )}
      <MobileRoomPrizeImage
        amIWinner={amIWinner}
        nobodyMakeAmove={winnerStatus === WinnerStatusEnum.UnusedRoomClosed}
      />

      <InfiniteMaxProgressBar
        value={Number(displayedPoints)}
        showAttention
        className="absolute bottom-20 left-1/2 z-50 w-[90%] max-w-72 -translate-x-1/2 sm:bottom-[-1rem]"
      />
    </>
  );
};

export default MobileFinishView;
